import React, { useRef, useEffect } from 'react';

/**
 * Ortak textarea + satır numarası componenti
 * Props:
 *  - value: textarea değeri
 *  - onChange: değişim fonksiyonu
 *  - placeholder: placeholder metni
 *  - style: ek stil (opsiyonel)
 *  - textareaProps: textarea'ya ekstra prop (opsiyonel)
 *  - lineNumbersRef: satır numarası için ref (opsiyonel)
 *  - textareaRef: textarea için ref (opsiyonel)
 *  - onScroll: textarea scroll event (opsiyonel)
 */
const defaultEditorStyle = {
  width: '100%',
  height: '100%',
  border: '1px solid #d0d7de',
  borderRadius: '4px',
  fontFamily: 'Consolas, Monaco, monospace',
  fontSize: '14px',
  lineHeight: '1.5',
  boxSizing: 'border-box',
  margin: 0,
  boxShadow: 'inset 0 1px 2px rgba(27, 31, 35, 0.075)',
  padding: '10px 10px 10px 50px',
  position: 'relative',
  backgroundColor: '#fff',
  color: '#24292e',
  resize: 'none',
  overflowY: 'auto',
};

function renderLineNumbers(text, lineNumbersRef, className) {
  if (!text) return null;
  const lines = text.split('\n');
  const totalLines = lines.length;
  const lineNumWidth = totalLines > 999 ? 50 : totalLines > 99 ? 40 : 30;
  return (
    <div
      ref={lineNumbersRef}
      className={className}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${lineNumWidth}px`,
        textAlign: 'right',
        color: '#6e7681',
        userSelect: 'none',
        fontFamily: 'Consolas, Monaco, monospace',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '1.5',
        height: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRight: '1px solid #e1e4e8',
        backgroundColor: '#f6f8fa',
        paddingTop: '10px',
        paddingBottom: '10px',
        zIndex: 1,
      }}
    >
      {lines.map((_, i) => (
        <div key={i} style={{
          lineHeight: '1.5',
          boxSizing: 'border-box',
          paddingRight: '10px',
          position: 'relative',
          color: (i + 1) % 5 === 0 ? '#24292e' : '#6e7681',
          fontWeight: (i + 1) % 10 === 0 ? '600' : '400',
          height: '21px',
        }}>{i + 1}</div>
      ))}
    </div>
  );
}

const TextareaWithLines = React.forwardRef(({
  value,
  onChange,
  placeholder,
  style = {},
  textareaProps = {},
  lineNumbersRef,
  textareaRef,
  onScroll,
  lineNumbersRefClassName,
}, ref) => {
  // textareaRef ve ref önceliği
  const internalTextareaRef = useRef();
  useEffect(() => {
    if (textareaRef && internalTextareaRef.current) {
      if (typeof textareaRef === 'function') textareaRef(internalTextareaRef.current);
      else textareaRef.current = internalTextareaRef.current;
    }
    if (ref && internalTextareaRef.current) {
      if (typeof ref === 'function') ref(internalTextareaRef.current);
      else ref.current = internalTextareaRef.current;
    }
  }, [textareaRef, ref]);

  return (
    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderLineNumbers(value, lineNumbersRef, lineNumbersRefClassName)}
      <textarea
        ref={internalTextareaRef}
        value={value}
        onChange={onChange}
        onScroll={onScroll}
        style={{ ...defaultEditorStyle, ...style }}
        placeholder={placeholder}
        {...textareaProps}
      />
    </div>
  );
});

export default TextareaWithLines; 